/**
 * IndexedDB persistence for chat sessions, backend context, message payloads,
 * favorites, and the chat-history archive.
 *
 * Stores:
 *   - `sessions`  — full session state (messages, thread cursors, timestamps)
 *   - `context`   — per-thread backend context snapshots (compound key)
 *   - `payload`   — message UISpec payloads (lean message pattern)
 *   - `favorites` — saved products
 *   - `history`   — the chat-history archive (past sessions); replaces the
 *                   previous localStorage backend so bulk history no longer
 *                   lives in the origin-shared localStorage.
 *
 * All operations are best-effort: IndexedDB unavailability is non-fatal.
 */

import type { SerializableChatMessage } from '../chat/types.js';
import type { PanelSource } from '../chat/panel-manager.js';
import type { UISpec } from './types.js';

// ---------------------------------------------------------------------------
// Data shapes stored in each object store
// ---------------------------------------------------------------------------

export interface SessionData {
  userId: string;
  appId: string;
  sessionId: string;
  messages: SerializableChatMessage[];
  currentThreadId: string | null;
  lastThreadId: string | null;
  createdAt: string;
  /** Thread IDs that produced panel content, in creation order. */
  panelThreads?: string[] | undefined;
  /** Legacy thumbnail-strip data kept optional for backward compatibility with older sessions. */
  thumbnailEntries?: Array<{ sku: string; imageUrl: string; threadId: string }> | undefined;
  /** Serialized panel HTML keyed by bot message ID (for panel snapshot restore). */
  panelSnapshotHtml?: Record<string, string> | undefined;
  /** Panel component type keyed by snapshot message ID. */
  panelSnapshotTypes?: Record<string, string> | undefined;
  /** Serializable panel rebuild source keyed by snapshot message ID. */
  panelSnapshotSources?: Record<string, PanelSource> | undefined;
  /** Latest panel AI-zone UISpec keyed by thread ID. */
  panelAiSpecByThread?: Record<string, UISpec> | undefined;
  /** SKU active when session was saved — prevents cross-SKU restore. */
  sku?: string | undefined;
}

export interface ContextData {
  sessionId: string;
  threadId: string;
  context: import('./types.js').BackendContext;
}

export interface PayloadData {
  threadId: string;
  messageId: string;
  uiSpec: UISpec;
}

export interface FavoriteData {
  userId: string;
  appId: string;
  sku: string;
  name?: string | undefined;
  imageUrl?: string | undefined;
  price?: string | undefined;
  savedAt: string;
}

/**
 * Minimal shape the history store needs for its compound key and scope index.
 * The full record (ChatHistorySessionRecord, owned by chat/history-storage.ts)
 * is stored opaquely on top of these fields — defined here to avoid a circular
 * import (history-storage imports this module).
 */
export interface HistoryRecordBase {
  appId: string;
  userId: string;
  /** The history record id (historyId). */
  id: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// GengageIndexedDB
// ---------------------------------------------------------------------------

const DB_NAME = 'gengage_assistant';
const DB_VERSION = 4;

const STORE_SESSIONS = 'sessions';
const STORE_CONTEXT = 'context';
const STORE_PAYLOAD = 'payload';
const STORE_FAVORITES = 'favorites';
const STORE_HISTORY = 'history';

/**
 * Hard cap on the number of UISpec payloads retained in the payload store.
 *
 * Payloads are written one-per-bot-message and never deleted in the normal
 * flow (rollback prunes contexts, not payloads), so the store grows unbounded
 * over a long-lived user. We sweep it on each open(): keys are
 * `[threadId, messageId]` where threadId is a UUIDv7 (time-sortable), so the
 * primary-key cursor visits oldest threads first and we prune from the front
 * until the store is back under the cap. Best-effort; failure is non-fatal.
 */
const MAX_PAYLOAD_ENTRIES = 500;

/**
 * Wrap an IDBRequest in a Promise.
 */
function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Wrap an IDBTransaction completion in a Promise.
 */
function transactionComplete(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new DOMException('Transaction aborted'));
  });
}

export class GengageIndexedDB {
  private _db: IDBDatabase | null = null;
  private _dbName: string;
  private _version: number;

  constructor(dbName: string = DB_NAME, version: number = DB_VERSION) {
    this._dbName = dbName;
    this._version = version;
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  async open(): Promise<IDBDatabase> {
    if (this._db) return this._db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this._dbName, this._version);
      // A blocked open does NOT abort: once the other tab closes, onsuccess still
      // fires later. Track settlement so a post-rejection success doesn't assign
      // this._db / prune on an abandoned instance and leak an open connection
      // (which would itself block future upgrades — the very thing we guard).
      let settled = false;

      request.onupgradeneeded = (event) => {
        const db = request.result;
        const oldVersion = (event as IDBVersionChangeEvent).oldVersion;

        if (oldVersion < 1) {
          // Fresh install — create stores with v3 schema directly
          db.createObjectStore(STORE_SESSIONS, { keyPath: ['userId', 'appId', 'sessionId'] });
          db.createObjectStore(STORE_CONTEXT, { keyPath: ['sessionId', 'threadId'] });
          const payloadStore = db.createObjectStore(STORE_PAYLOAD, {
            keyPath: ['threadId', 'messageId'],
          });
          payloadStore.createIndex('threadId', 'threadId', { unique: false });
          db.createObjectStore(STORE_FAVORITES, { keyPath: ['userId', 'appId', 'sku'] });
        }

        if (oldVersion >= 1 && oldVersion < 2) {
          // Upgrade from v1 — drop old stores, recreate with new keys
          if (db.objectStoreNames.contains(STORE_SESSIONS)) db.deleteObjectStore(STORE_SESSIONS);
          if (db.objectStoreNames.contains(STORE_PAYLOAD)) db.deleteObjectStore(STORE_PAYLOAD);
          db.createObjectStore(STORE_SESSIONS, { keyPath: ['userId', 'appId', 'sessionId'] });
          const payloadStore = db.createObjectStore(STORE_PAYLOAD, {
            keyPath: ['threadId', 'messageId'],
          });
          payloadStore.createIndex('threadId', 'threadId', { unique: false });
          // Ensure context store exists (may be absent in early v1 schemas)
          if (!db.objectStoreNames.contains(STORE_CONTEXT)) {
            db.createObjectStore(STORE_CONTEXT, { keyPath: ['sessionId', 'threadId'] });
          }
        }

        if (oldVersion < 3) {
          if (!db.objectStoreNames.contains(STORE_FAVORITES)) {
            db.createObjectStore(STORE_FAVORITES, { keyPath: ['userId', 'appId', 'sku'] });
          }
        }

        if (oldVersion < 4) {
          if (!db.objectStoreNames.contains(STORE_HISTORY)) {
            const historyStore = db.createObjectStore(STORE_HISTORY, { keyPath: ['appId', 'userId', 'id'] });
            // Scope index: list/migrate all history records for one app+user.
            historyStore.createIndex('scope', ['appId', 'userId'], { unique: false });
          }
        }
      };

      request.onsuccess = () => {
        // If onblocked already rejected this open(), the caller has moved on in
        // degraded mode — close this now-orphaned connection instead of caching
        // it (a leaked connection would block the next tab's schema upgrade).
        if (settled) {
          request.result.close();
          return;
        }
        settled = true;
        this._db = request.result;
        // Yield to a newer tab that wants to upgrade the schema: close our
        // connection so its versionchange transaction can proceed instead of
        // hanging on a blocked event. After this we degrade to no persistence.
        this._db.onversionchange = () => {
          this._db?.close();
          this._db = null;
        };
        // Prune the payload store on open so it cannot grow without bound.
        void this._prunePayloadStore();
        resolve(this._db);
      };

      // Another tab holds an older connection open and is blocking this upgrade.
      // Reject (settle) rather than hang forever — callers treat a rejected
      // open() as "no persistence" and continue in degraded mode. This mirrors
      // request-response-cache.ts, which resolves null for the same reason; we
      // reject here to preserve the non-null open() return contract.
      request.onblocked = () => {
        if (settled) return;
        settled = true;
        reject(request.error ?? new DOMException('IndexedDB open blocked', 'AbortError'));
      };

      request.onerror = () => {
        if (settled) return;
        settled = true;
        reject(request.error);
      };
    });
  }

  close(): void {
    this._db?.close();
    this._db = null;
  }

  // -------------------------------------------------------------------------
  // Sessions
  // -------------------------------------------------------------------------

  async saveSession(data: SessionData): Promise<void> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_SESSIONS, 'readwrite');
    tx.objectStore(STORE_SESSIONS).put(data);
    await transactionComplete(tx);
  }

  async loadSession(userId: string, appId: string, sessionId: string): Promise<SessionData | null> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_SESSIONS, 'readonly');
    const result = await requestToPromise(tx.objectStore(STORE_SESSIONS).get([userId, appId, sessionId]));
    return (result as SessionData | undefined) ?? null;
  }

  // -------------------------------------------------------------------------
  // Context (compound key: [sessionId, threadId])
  // -------------------------------------------------------------------------

  async saveContext(data: ContextData): Promise<void> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_CONTEXT, 'readwrite');
    tx.objectStore(STORE_CONTEXT).put(data);
    await transactionComplete(tx);
  }

  async loadContext(sessionId: string, threadId: string): Promise<ContextData | null> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_CONTEXT, 'readonly');
    const result = await requestToPromise(tx.objectStore(STORE_CONTEXT).get([sessionId, threadId]));
    return (result as ContextData | undefined) ?? null;
  }

  /**
   * Delete all context entries for a session whose threadId is lexicographically
   * greater than the given threadId. Used during rollback to prune future branches.
   *
   * Thread IDs are UUIDv7 (lexicographically sortable by time), so string
   * comparison is sufficient.
   */
  async deleteContextsAfterThread(sessionId: string, threadId: string): Promise<void> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_CONTEXT, 'readwrite');
    const store = tx.objectStore(STORE_CONTEXT);

    // Open cursor over all entries and filter by sessionId + threadId comparison
    const request = store.openCursor();

    await new Promise<void>((resolve, reject) => {
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        const entry = cursor.value as ContextData;
        if (entry.sessionId === sessionId && entry.threadId > threadId) {
          try {
            cursor.delete();
          } catch {
            // cursor.delete() may fail on read-only or version-change transactions — non-fatal
          }
        }
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });

    await transactionComplete(tx);
  }

  /**
   * Load the most recent context for a session (latest threadId).
   * Uses lexicographic ordering of UUIDv7 threadIds for chronological sort.
   */
  async loadLatestContext(sessionId: string): Promise<ContextData | null> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_CONTEXT, 'readonly');
    const store = tx.objectStore(STORE_CONTEXT);

    // UUIDv7 thread IDs are ASCII hex+hyphens, so \uffff is a safe upper bound
    const range = IDBKeyRange.bound([sessionId, ''], [sessionId, '\uffff']);

    return new Promise((resolve, reject) => {
      const request = store.openCursor(range, 'prev');
      request.onsuccess = () => {
        const cursor = request.result;
        resolve(cursor ? (cursor.value as ContextData) : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // -------------------------------------------------------------------------
  // Payload
  // -------------------------------------------------------------------------

  async savePayload(data: PayloadData): Promise<void> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_PAYLOAD, 'readwrite');
    tx.objectStore(STORE_PAYLOAD).put(data);
    await transactionComplete(tx);
  }

  async loadPayload(threadId: string, messageId: string): Promise<PayloadData | null> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_PAYLOAD, 'readonly');
    const result = await requestToPromise(tx.objectStore(STORE_PAYLOAD).get([threadId, messageId]));
    return (result as PayloadData | undefined) ?? null;
  }

  async loadPayloadsByThread(threadId: string): Promise<PayloadData[]> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_PAYLOAD, 'readonly');
    const index = tx.objectStore(STORE_PAYLOAD).index('threadId');
    const results: PayloadData[] = [];

    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.only(threadId));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve(results);
          return;
        }
        results.push(cursor.value as PayloadData);
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // -------------------------------------------------------------------------
  // Favorites
  // -------------------------------------------------------------------------

  async saveFavorite(data: FavoriteData): Promise<void> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_FAVORITES, 'readwrite');
    tx.objectStore(STORE_FAVORITES).put(data);
    await transactionComplete(tx);
  }

  async removeFavorite(userId: string, appId: string, sku: string): Promise<void> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_FAVORITES, 'readwrite');
    tx.objectStore(STORE_FAVORITES).delete([userId, appId, sku]);
    await transactionComplete(tx);
  }

  async loadFavorites(userId: string, appId: string): Promise<FavoriteData[]> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_FAVORITES, 'readonly');
    const all = await requestToPromise(tx.objectStore(STORE_FAVORITES).getAll());
    return (all as FavoriteData[]).filter((f) => f.userId === userId && f.appId === appId);
  }

  async isFavorite(userId: string, appId: string, sku: string): Promise<boolean> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_FAVORITES, 'readonly');
    const result = await requestToPromise(tx.objectStore(STORE_FAVORITES).get([userId, appId, sku]));
    return result !== undefined;
  }

  // -------------------------------------------------------------------------
  // History archive
  // -------------------------------------------------------------------------

  async saveHistory(record: HistoryRecordBase): Promise<void> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_HISTORY, 'readwrite');
    tx.objectStore(STORE_HISTORY).put(record);
    await transactionComplete(tx);
  }

  async loadHistory<T extends HistoryRecordBase>(appId: string, userId: string, id: string): Promise<T | null> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_HISTORY, 'readonly');
    const result = await requestToPromise(tx.objectStore(STORE_HISTORY).get([appId, userId, id]));
    return (result as T | undefined) ?? null;
  }

  async deleteHistory(appId: string, userId: string, id: string): Promise<void> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_HISTORY, 'readwrite');
    tx.objectStore(STORE_HISTORY).delete([appId, userId, id]);
    await transactionComplete(tx);
  }

  /** All history records for one app+user scope (used to hydrate the in-memory index and to migrate). */
  async loadHistoryByScope<T extends HistoryRecordBase>(appId: string, userId: string): Promise<T[]> {
    const db = this._requireDb();
    const tx = db.transaction(STORE_HISTORY, 'readonly');
    const index = tx.objectStore(STORE_HISTORY).index('scope');
    const all = await requestToPromise(index.getAll(IDBKeyRange.only([appId, userId])));
    return (all as T[]) ?? [];
  }

  // -------------------------------------------------------------------------
  // Internal
  // -------------------------------------------------------------------------

  private _requireDb(): IDBDatabase {
    if (!this._db) {
      throw new Error('GengageIndexedDB: database not open. Call open() first.');
    }
    return this._db;
  }

  /**
   * Cap the payload store at MAX_PAYLOAD_ENTRIES by pruning the oldest entries.
   *
   * Self-contained: relies only on the payload store's compound key
   * `[threadId, messageId]`. Because threadId is a UUIDv7, the default
   * primary-key cursor visits oldest threads first, so we count, then delete
   * from the front until the store is back under the cap. Best-effort — any
   * failure (db absent, transaction error) is swallowed.
   */
  async _prunePayloadStore(): Promise<void> {
    if (!this._db) return;
    try {
      const tx = this._db.transaction(STORE_PAYLOAD, 'readwrite');
      const store = tx.objectStore(STORE_PAYLOAD);

      // Single cursor pass: count by walking primary-key order (oldest first)
      // and, once we know how far the store overflows the cap, delete from the
      // front. Doing the count and delete in one transaction avoids the IDB
      // auto-commit hazard of awaiting between two requests on the same tx.
      await new Promise<void>((resolve, reject) => {
        const keys: IDBValidKey[] = [];
        const cursorRequest = store.openCursor();
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (cursor) {
            keys.push(cursor.primaryKey);
            cursor.continue();
            return;
          }
          // Cursor exhausted — primaryKey order is oldest-first, so delete the
          // leading overflow slice to bring the store back under the cap.
          const overflow = keys.length - MAX_PAYLOAD_ENTRIES;
          for (let i = 0; i < overflow; i++) {
            const key = keys[i];
            if (key !== undefined) store.delete(key);
          }
          resolve();
        };
        cursorRequest.onerror = () => reject(cursorRequest.error);
      });

      await transactionComplete(tx);
    } catch {
      // Pruning is best-effort; never let a sweep failure break persistence.
    }
  }
}
